import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Input, Select, SelectItem, Spinner } from '@nextui-org/react';
import { IconArrowLeft, IconEye, IconEyeOff } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { IconImageEdit } from '../../assets/icons';
import { Constants, classNames, formatAmount, fromPhotos, getOnlyNumbers } from '../../utils';
import { AuthService } from '../../services';
import { usePayment } from '../../hooks/usePayment';
import CountryFlag from '../../components/country-flag';
import Render from '../../components/render';
import AcquirePlan from '../payment/acquire-plan';

const STEP = {
  DATA: 1,
  PAYMENT: 2,
}

const RegisterTeacher = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const toggleVisibility = () => setIsVisible(!isVisible);
  const { dniTypes, form, initiatePayment, isLoading, isValidForm, onChange, onSuccess, onUpload, phoneCodes, plans, setShowPaymentModal, setStep, showPaymentModal, step, submit, user } = useForm();
  const _isValidForm = isValidForm(false);

  const onChangePlan = (plan) => {
    onChange(plan.id, 'plan_id');
    onChange(plan.price, 'amount');
  }

  const request = useMemo(() => ({
    amount: form.amount,
    plan_id: form.plan_id,
    plan: plans.find(x => x.id === form.plan_id),
  }), [form, plans]);

  return (
    <div>
      <Render condition={showPaymentModal}>
        <AcquirePlan
          user={user}
          plan={request.plan}
          initPayment={initiatePayment}
          onSuccess={onSuccess}
          onClose={() => setShowPaymentModal(false)}
        />
      </Render>

      {isLoading && (
        <div className="w-screen h-screen fixed inset-0 z-[70] flex justify-center items-center bg-white/30">
          <Spinner />
        </div>
      )}

      <div className="min-h-screen px-4 md:px-0 flex justify-center">
        <div
          className="absolute top-4 left-4 cursor-pointer"
          onClick={() => step === STEP.PAYMENT ? setStep(STEP.DATA) : navigate('/register')}
        >
          <IconArrowLeft />
        </div>

        <div className="w-[420px] py-4 px-0">
          <p className="text-center text-lg font-bold text-secondary mb-8">Registro de profesor</p>

          <div className="flex flex-row items-start gap-4">
            <div className="flex-1 text-center">
              <div className="h-[6px] rounded bg-secondary" />
              <span className="text-sm font-semibold text-gray">Datos</span>
            </div>
            <div className="flex-1 text-center">
              <div className={classNames('h-[6px] rounded', step === STEP.PAYMENT ? 'bg-secondary' : 'bg-gray')} />
              <span className="text-sm font-semibold text-gray">Plan</span>
            </div>
          </div>

          {step === STEP.DATA && (
            <>
              <label htmlFor="photo" className="cursor-pointer text-center border-2 border-gray w-24 h-24 rounded-lg flex justify-center items-center self-center my-4 mx-auto overflow-hidden">
                {!!form.photoUrl ? (
                  <img className="w-full h-full object-cover" src={form.photoUrl} alt="Foto del profesor" />
                ) : (
                  <img className="w-16 h-16 object-contain opacity-50" src={IconImageEdit} alt="Foto del profesor" />
                )}
                <input id="photo" name="photo" type="file" accept="image/*" hidden onChange={onUpload} />
              </label>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row gap-4 [&>div]:flex-1">
                  <Input
                    label="Nombre"
                    labelPlacement="outside"
                    placeholder=" "
                    variant="bordered"
                    radius="sm"
                    value={form.name}
                    onValueChange={v => onChange(v, 'name')}
                  />
                  <Input
                    label="Apellido"
                    labelPlacement="outside"
                    placeholder=" "
                    variant="bordered"
                    radius="sm"
                    value={form.lastname}
                    onValueChange={v => onChange(v, 'lastname')}
                  />
                </div>
                <div className="flex flex-col md:flex-row gap-4 [&>div]:flex-1">
                  <Input
                    label="DNI/C.I"
                    labelPlacement="outside"
                    placeholder=" "
                    startContent={(
                      <Select
                        aria-label="Tipo de documento"
                        variant="flat"
                        classNames={{ trigger: 'border-0' }}
                        disallowEmptySelection={true}
                        selectedKeys={!!form.document_type_id ? [form.document_type_id] : []}
                        onSelectionChange={v => onChange(v.currentKey, 'document_type_id')}
                      >
                        {dniTypes.map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </Select>
                    )}
                    variant="bordered"
                    radius="sm"
                    classNames={{ innerWrapper: 'overflow-hidden', inputWrapper: 'pl-0' }}
                    value={form.document}
                    onValueChange={v => onChange(getOnlyNumbers(v), 'document')}
                  />
                  <Input
                    label="Teléfono"
                    labelPlacement="outside"
                    placeholder=" "
                    startContent={(
                      <Select
                        aria-label="Código de área"
                        variant="flat"
                        classNames={{ trigger: 'border-0 max-w-32 pl-1 pr-0' }}
                        disallowEmptySelection={true}
                        selectedKeys={(!!form.phonecode_id && !!phoneCodes.length) ? [form.phonecode_id] : []}
                        onSelectionChange={v => onChange(v.currentKey, 'phonecode_id')}
                        startContent={<CountryFlag phoneCodeId={form.phonecode_id} phoneCodes={phoneCodes} />}
                      >
                        {phoneCodes.map((item) => (
                          <SelectItem
                            key={item.value}
                            value={item.value}
                            startContent={<CountryFlag src={item.url} />}
                            classNames={{ title: 'min-w-48' }}
                          >
                            {item.label}
                          </SelectItem>
                        ))}
                      </Select>
                    )}
                    variant="bordered"
                    radius="sm"
                    classNames={{ innerWrapper: 'overflow-hidden', inputWrapper: 'pl-0' }}
                    value={form.phone}
                    onValueChange={v => onChange(getOnlyNumbers(v), 'phone')}
                  />
                </div>
                <div className="flex flex-col md:flex-row gap-4 [&>div]:flex-1">
                  <Input
                    label="Correo"
                    labelPlacement="outside"
                    placeholder=" "
                    variant="bordered"
                    radius="sm"
                    value={form.email}
                    onValueChange={v => onChange(v, 'email')}
                  />
                  <div className="hidden md:flex" />
                </div>
                <Input
                  label="Nombre del centro educativo"
                  labelPlacement="outside"
                  placeholder=" "
                  variant="bordered"
                  radius="sm"
                  value={form.educational_center}
                  onValueChange={v => onChange(v, 'educational_center')}
                />
                <div className="flex flex-col md:flex-row gap-4 [&>div]:flex-1">
                  <Input
                    label="Contraseña"
                    labelPlacement="outside"
                    placeholder=" "
                    variant="bordered"
                    radius="sm"
                    tabIndex={0}
                    value={form.password}
                    onValueChange={v => onChange(v, 'password')}
                    type={isVisible ? "text" : "password"}
                    endContent={
                      <button className="focus:outline-none" type="button" onClick={toggleVisibility} tabIndex={1}>
                        {isVisible ? (
                          <IconEyeOff className="w-6 h-6" />
                        ) : (
                          <IconEye className="w-6 h-6" />
                        )}
                      </button>
                    }
                  />
                  <Input
                    label="Repetir contraseña"
                    labelPlacement="outside"
                    placeholder=" "
                    variant="bordered"
                    radius="sm"
                    tabIndex={0}
                    value={form.password_confirmation}
                    onValueChange={v => onChange(v, 'password_confirmation')}
                    type={isVisible ? "text" : "password"}
                    endContent={
                      <button className="focus:outline-none" type="button" onClick={toggleVisibility} tabIndex={1}>
                        {isVisible ? (
                          <IconEyeOff className="w-6 h-6" />
                        ) : (
                          <IconEye className="w-6 h-6" />
                        )}
                      </button>
                    }
                  />
                </div>
              </div>

              <div className="flex justify-center">
                <Button
                  type="button"
                  className="w-1/2 bg-green text-white text-md my-12"
                  onPress={() => _isValidForm ? setStep(STEP.PAYMENT) : isValidForm()}
                >
                  Siguiente
                </Button>
              </div>
            </>
          )}

          {step === STEP.PAYMENT && (
            <>
              <main className="flex my-12 justify-center">
                {plans.map(plan => {
                  const isFree = plan.price === 0;
                  const description = String(plan?.description ?? '')
                    .replace(':date', moment().add(1, 'week').format('DD/MM/YYYY'))
                    .replace(':price', '$' + formatAmount(plan?.price));

                  return (
                    <div
                      key={plan.id}
                      className={classNames(
                        'rounded-xl p-2 cursor-pointer shadow-md',
                        form.plan_id === plan.id ? 'bg-[#C6FFDE]' : 'bg-white'
                      )}
                      onClick={() => onChangePlan(plan)}
                    >
                      <div className="flex justify-start items-start gap-4">
                        <div className="bg-[#3E82C3] p-2 rounded-lg">
                          <img className="w-7 h-7 object-contain" src={fromPhotos(plan?.icon)} alt="Plan" />
                        </div>

                        <div>
                          <p className="font-bold text-sm leading-4 mb-0">{plan?.name}</p>
                          <p className="font-semibold text-lg text-secondary mb-0">
                            { isFree ? 'Gratuito' : formatAmount(plan?.price, '$') }
                          </p>
                        </div>
                      </div>

                      <p className="mt-4 font-medium text-xs text-black whitespace-pre-line">
                        {description}
                      </p>
                    </div>
                  )
                })}
              </main>

              <div className="flex flex-col items-center" onClick={submit}>
                <Button
                  type="button"
                  className="w-1/2 bg-green text-white text-md"
                  onPress={submit}
                  isDisabled={isLoading || !_isValidForm}
                >
                  Registrarme
                </Button>

                <Button
                  type="button"
                  variant="flat"
                  className="w-1/2 text-md mt-4"
                  onPress={() => setStep(STEP.DATA)}
                  isDisabled={isLoading}
                >
                  Atrás
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const useForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState(STEP.DATA);
  const [form, setForm] = useState({
    level_id: Constants.USER.LEVELS.PROFESSOR,
    photo: {},
    photoUrl: '',
    name: '',
    lastname: '',
    document_type_id: '',
    document: '',
    phonecode_id: '',
    phone: '',
    email: '',
    educational_center: '',
    password: '',
    password_confirmation: '',
    plan_id: undefined,
    amount: 0,
    hasFile: true,
  });
  const [user, setUser] = useState();
  const [dniTypes, setDniTypes] = useState([]);
  const [phoneCodes, setPhoneCodes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [townships, setTownships] = useState([]);
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const { initiatePayment } = usePayment({
    // Callback que se llama cuando se completa el pago
    onCompletePayment: (data) => {
      if (!data.success) return deleteUser(data.paymentData.user_id)
        onSuccess(data.paymentData);
      setTimeout(() => window.location.reload(), 1000);
    }
  })

  const onUpload = (e) => {
    setForm(s => ({
      ...s,
      photo: e.target.files[0],
      photoUrl: URL.createObjectURL(e.target.files[0]),
    }));
  }

  const onChange = (value, target) => {
    setForm(s => ({ ...s, [target]: value }));
  }

  const isValidForm = (showError = true) => {
    const onError = (msg) => {
      if (showError) toast.error(msg);
      return false;
    }

    if (step === STEP.DATA) {
      if (!form.name)
        return onError('Debe indicar el nombre');

      if (!form.lastname)
        return onError('Debe indicar el apellido');

      if (!form.document_type_id)
        return onError('Debe indicar el tipo de documento');

      if (!form.document)
        return onError('Debe indicar el número de cédula');

      if (!form.phonecode_id)
        return onError('Debe indicar el código de área');

      if (!form.phone)
        return onError('Debe indicar el número telefónico');

      if (!form.email)
        return onError('Debe indicar el correo');

      if (!form.email.match(/^[A-Za-z0-9._%+-]+@[a-zA-Z_]+?\.[a-zA-Z]{2,}$/))
        return onError('El correo debe tener un formato válido');

      if (!form.password)
        return onError('Debe indicar la contraseña');

      if (form.password !== form.password_confirmation)
        return onError('Las contraseñas no coinciden');

    } else {
      if (!form.plan_id)
        return onError('Debe seleccionar un plan');
    }

    return true;
  }

  const deleteUser = async (userId) => {
    try {
      await AuthService.destroyUser(userId);
    } catch (error) {
      toast.error(String(error) || 'Ocurrió un error inesperado');
    }
  }

  const submit = async () => {
    if (!isValidForm()) return;
    setIsLoading(true);

    try {
      if (!user) {
        const res = await AuthService.register(form);
        // Se almacena el usuario registrado por si el mismo sale del modal del pago (no vuelve a registrarse)
        setUser(res.user);
        onSuccess(res);
      }
      // setShowPaymentModal(true);

    } catch (error) {
      // toast.error(error || 'Ocurrió un error inesperado');
    }
    setIsLoading(false);
  }

  const onSuccess = (res) => {
    if (res?.user) {
      dispatch({ type: 'SET_USER', payload: res?.user });
    } else {
      // La respuesta fue exitosa pero no devolvió el usuario
      toast.error('Ocurrió un error inesperado. Por favor, intente iniciar sesión');
    }
    setTimeout(() => {
      navigate('/');
      window.location.reload();
    }, 100);
  }

  const getSelects = async () => {
    const toSelect = (items) => items?.map(x => ({ ...x, value: String(x.id), label: x.name })) || [];

    try {
      const selects = await AuthService.getSelects();
      setDniTypes(toSelect(selects.document_types));
      setPhoneCodes(toSelect(selects.phonecodes));
      setCountries(toSelect(selects.countries));
      setStates(toSelect(selects.states));
      setTownships(toSelect(selects.municipalities));
      setPlans(toSelect(selects.plans.filter((x) => x.for === Constants.PLAN_FOR.TEACHER && x.price === 0)));

    } catch (error) {
      toast.error(String(error) || 'Ocurrió un error inesperado');
      return false;
    }
  }

  useEffect(() => {
    getSelects();
  }, []);

  return {
    countries,
    dniTypes,
    form,
    initiatePayment,
    isLoading,
    isValidForm,
    onChange,
    onUpload,
    phoneCodes,
    onSuccess,
    plans,
    setShowPaymentModal,
    setStep,
    showPaymentModal,
    states,
    step,
    submit,
    townships: townships.filter(x => x.state_id === +form.state_id),
    user,
  };
}

export default RegisterTeacher;